// src/components/DashboardHeader.jsx
import React from 'react';
import {
  Box,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,          // NEW: for dataset multi-select
} from '@mui/material';
import TotalInterviewsPill from './tiles/TotalInterviewsPill';

/* ⬇⬇ NEW: pull the global showTables state from context */
import { useTableVisibility } from '../context/TableVisibilityContext';

export default function DashboardHeader({
  mode,
  onModeChange,
  options,
  selected,
  onSelectedChange,
  headerSummary,
  showReceipts,
  onShowReceiptsChange,
  dateRange,
  onDateRangeChange,
  optionCounts = {},          // ✅ NEW: counts per option
  // NEW: dataset props
  datasetOptions = [],
  selectedDatasets = [],
  onSelectedDatasetsChange,
}) {
  const handleMode = (_, val) => {
    if (val) onModeChange(val);
  };
  const titlePrefix = mode === 'product' ? 'Product:' : 'Job:';

  // make Date Range controlled from parent (optional; keeps future wiring easy)
  const [range, setRange] = React.useState(dateRange || 'last4');
  React.useEffect(() => {
    setRange(dateRange || 'last4');
  }, [dateRange]);
  const handleRange = (e) => {
    setRange(e.target.value);
    onDateRangeChange?.(e.target.value);
  };

  // job-only: related chips (unchanged)
  const relatedJobs = mode === 'job' ? headerSummary?.related_jobs || [] : [];

  /* ⬇⬇ NEW: global Show tables toggle state */
  const { showTables, setShowTables } = useTableVisibility();

  // Helper to compute the Autocomplete "value" for datasets
  const datasetValue = React.useMemo(() => {
    if (!datasetOptions?.length || !selectedDatasets?.length) return [];
    const selectedSet = new Set(selectedDatasets);
    return datasetOptions.filter((opt) => selectedSet.has(opt.key));
  }, [datasetOptions, selectedDatasets]);

  return (
    <Box>
      {/* Controls row */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
      >
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={handleMode}>
          <ToggleButton value="product">Product view</ToggleButton>
          <ToggleButton value="job">Job view</ToggleButton>
        </ToggleButtonGroup>

        <Autocomplete
          size="small"
          disableClearable
          options={options}
          sx={{ minWidth: 320 }}
          value={selected || ''}
          onChange={(_, v) => onSelectedChange(v)}
          getOptionLabel={(option) => {
            if (!option) return '';
            const count = optionCounts?.[option];
            if (typeof count === 'number') {
              return `${option} (${count})`;
            }
            return option;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={mode === 'product' ? 'Select product' : 'Select job'}
            />
          )}
        />

        {/* Date filter (back on the bar) */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="range-label">Date range</InputLabel>
          <Select labelId="range-label" label="Date range" value={range} onChange={handleRange}>
            <MenuItem value="last4">Last 4 months</MenuItem>
            <MenuItem value="last12">Last 12 months</MenuItem>
            <MenuItem value="ytd">Year to date</MenuItem>
            <MenuItem value="all">All time</MenuItem>
          </Select>
        </FormControl>

        {/* NEW: Interview datasets multi-select */}
        <Box sx={{ minWidth: 260 }}>
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={datasetOptions || []}
            value={datasetValue}
            getOptionLabel={(option) => option.label || option.slug || option.key}
            onChange={(_, newValues) => {
              const keys = (newValues || []).map((v) => v.key);
              onSelectedDatasetsChange?.(keys);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label || option.slug || option.key}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Interview datasets"
                placeholder="Select datasets"
              />
            )}
          />
        </Box>

        {/* Receipts toggle */}
        <FormControlLabel
          sx={{ ml: { xs: 0, md: 1 } }}
          control={
            <Switch
              checked={!!showReceipts}
              onChange={(e) => onShowReceiptsChange?.(e.target.checked)}
              size="medium"
            />
          }
          label="Show receipts"
        />

        {/* ⬇⬇ NEW: Show tables toggle (global) */}
        <FormControlLabel
          sx={{ ml: { xs: 0, md: 1 } }}
          control={
            <Switch
              checked={!!showTables}
              onChange={(e) => setShowTables(e.target.checked)}
              size="medium"
            />
          }
          label="Show tables"
        />
      </Stack>

      {/* Big title */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {titlePrefix} {selected || '—'}
        </Typography>

        {/* Interviews pill UNDER the title (no percentage; total only) */}
        <Box sx={{ mt: 1 }}>
          <TotalInterviewsPill count={headerSummary?.interview_count ?? 0} />
        </Box>

        {/* “What changed…” text (kept), NO "View interviews" link */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {headerSummary?.changes_note || ''}
        </Typography>
      </Box>

      {/* Related jobs (Job view only) */}
      {mode === 'job' && relatedJobs.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Related jobs
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {relatedJobs.map((j, i) => (
              <Chip
                key={i}
                size="small"
                label={`${j.title} (${j.count} · ${j.percent ?? 0}%)`}
                onClick={() => onSelectedChange(j.title)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
